if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLElement {
      connectedCallback() {
        this.primaryNav = this.querySelector('[data-product-tabs-nav-primary]');
        this.railNav = this.querySelector('[data-product-tabs-nav-rail]');
        this.primaryNavWrapper = this.querySelector('[data-product-tabs-nav-wrapper-primary]');
        this.railNavWrapper = this.querySelector('[data-product-tabs-nav-wrapper-rail]');
        this.primaryTabs = Array.from(this.primaryNav?.querySelectorAll('[role="tab"]') || []);
        this.railTabs = Array.from(this.railNav?.querySelectorAll('[role="tab"]') || []);
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

        if (!this.primaryNav || this.primaryTabs.length === 0) return;

        this.classList.add('has-side-rail');
        this.setupSideRailObserver();
        this.mediaQuery = window.matchMedia('(min-width: 750px)');
        this.onMediaQueryChange = () => this.updateScrollHints();
        this.mediaQuery.addEventListener('change', this.onMediaQueryChange);

        this.primaryTabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            const index = parseInt(tab.dataset.tabIndex, 10);
            this.activateTab(index, { source: 'primary', scroll: false });
          });
        });

        this.railTabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            const index = parseInt(tab.dataset.tabIndex, 10);
            this.activateTab(index, { source: 'rail', scroll: true });
          });
        });

        this.primaryNav.addEventListener('scroll', () => this.updateScrollHints(), { passive: true });
        this.updateScrollHints();

        const initial =
          this.primaryTabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.primaryTabs[0];
        if (initial) {
          const index = parseInt(initial.dataset.tabIndex, 10);
          this.activateTab(index, { source: 'primary', scroll: false });
        }
      }

      disconnectedCallback() {
        this.sideRailObserver?.disconnect();
        this.mediaQuery?.removeEventListener('change', this.onMediaQueryChange);
      }

      setupSideRailObserver() {
        this.sideRailObserver = new IntersectionObserver(
          ([entry]) => {
            this.classList.toggle('is-active', entry.isIntersecting);
          },
          { threshold: 0 }
        );
        this.sideRailObserver.observe(this);
      }

      isDesktop() {
        return this.mediaQuery?.matches;
      }

      getHeaderHeight() {
        const value = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
        return parseFloat(value) || 0;
      }

      scrollToAnchor(anchor) {
        if (!anchor) return;

        const headerHeight = this.getHeaderHeight();
        const targetTop = anchor.getBoundingClientRect().top + window.scrollY - headerHeight;

        if (Math.abs(window.scrollY - targetTop) > 10) {
          window.scrollTo({ top: targetTop, behavior: 'smooth' });
        }
      }

      activateTab(index, { source = 'primary', scroll = true } = {}) {
        if (index < 0 || index >= this.primaryTabs.length) return;

        this.primaryTabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
        });

        this.railTabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
        });

        this.panels.forEach((panel, i) => {
          const active = i === index;
          if (active) {
            panel.removeAttribute('hidden');
            this.initVideosInPanel(panel);
          } else {
            panel.setAttribute('hidden', '');
          }
        });

        if (scroll && (source === 'rail' || source === 'external')) {
          this.scrollToAnchor(this.primaryNavWrapper || this);
        }

        if (this.railTabs[index]) {
          this.railTabs[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }

      activateTabByTitle(title, { scroll = true } = {}) {
        if (!title || this.primaryTabs.length === 0) return false;

        const normalized = String(title).trim().toLowerCase();
        const index = this.primaryTabs.findIndex(
          (tab) => tab.textContent.trim().toLowerCase() === normalized
        );

        if (index === -1) return false;

        this.activateTab(index, { source: 'external', scroll });
        return true;
      }

      updateScrollHints() {
        if (!this.primaryNavWrapper || !this.primaryNav) {
          return;
        }

        if (this.isDesktop()) {
          this.primaryNavWrapper.classList.remove(
            'product-tabs__nav-wrapper--scroll-left',
            'product-tabs__nav-wrapper--scroll-right'
          );
          return;
        }

        const { scrollLeft, scrollWidth, clientWidth } = this.primaryNav;
        this.primaryNavWrapper.classList.toggle('product-tabs__nav-wrapper--scroll-left', scrollLeft > 0);
        this.primaryNavWrapper.classList.toggle(
          'product-tabs__nav-wrapper--scroll-right',
          scrollLeft + clientWidth < scrollWidth - 1
        );
      }

      initVideosInPanel(panel) {
        if (panel.dataset.videosInitialized === 'true') return;
        panel.dataset.videosInitialized = 'true';
      }
    }
  );

  document.addEventListener('click', (event) => {
    const badge = event.target.closest('.jdgm-preview-badge');
    if (!badge) return;

    if (badge.closest('product-tabs')) return;

    const productContext = badge.closest('.product__info-container, .product, [data-section-type="product"]');
    if (!productContext) return;

    const productTabs = document.querySelector('product-tabs[data-reviews-tab-title]');
    if (!productTabs) return;

    const tabTitle = productTabs.dataset.reviewsTabTitle;
    if (!tabTitle) return;

    if (productTabs.activateTabByTitle(tabTitle, { scroll: true })) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
}
